import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Bell, Clock, MapPin, CheckCircle, AlertTriangle, RefreshCw, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationsTab = () => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/employer/notifications`, { credentials: 'include' });
      if (res.ok) setNotifications(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const handleClearAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer/notifications/clear`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || t("notificationsCleared") || "Notifications cleared and archived");
        setNotifications([]);
        setShowClearDialog(false);
      }
    } catch (e) { toast.error("Failed to clear notifications"); }
  };

  const lateNotifications = notifications.filter(n => n.type === "late_checkin");

  const formatTimestamp = (ts) => {
    try {
      return format(new Date(ts), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return ts;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {t("attendanceAlerts") || "Attendance Alerts"} {lateNotifications.length > 0 && <span className="text-muted-foreground font-normal text-base">({lateNotifications.length})</span>}
            </CardTitle>
            <CardDescription>
              {t("attendanceAlertsDesc") || "Late check-ins indicate potential scheduling or transportation issues. Tracking these helps identify patterns and improve team punctuality."}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchNotifications} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t("refresh") || "Refresh"}
            </Button>
            {lateNotifications.length > 0 && (
              <Button variant="destructive" size="sm" onClick={() => setShowClearDialog(true)}>
                <Trash2 className="w-4 h-4 mr-2" />
                {t("clearAll") || "Clear All"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">{t("loading") || "Loading..."}</div>
          ) : lateNotifications.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">{t("noLateCheckIns") || "No late check-ins in the last 30 days"}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("greatJobOnTime") || "Great job keeping your team on time!"}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {lateNotifications.map((notification, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-xl border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{notification.employee_name}</span>
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300">{t("lateCheckIn") || "Late Check-in"}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(notification.timestamp)}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {notification.location}
                        </p>
                        {notification.late_reason && (
                          <div className="mt-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                            <p className="text-sm font-medium text-yellow-800">{t("reasonProvided") || "Reason provided:"}</p>
                            <p className="text-sm text-yellow-700 mt-1">{notification.late_reason}</p>
                          </div>
                        )}
                        {!notification.late_reason && (
                          <div className="mt-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                            <p className="text-sm text-gray-500 italic">{t("noReasonProvided") || "No reason provided"}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clearNotifications") || "Clear All Attendance Alerts?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("clearNotificationsDesc") || "This will archive all late notifications. They will still be available in the Audit History tab for compliance and review purposes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-destructive text-destructive-foreground">
              {t("clearAll") || "Clear All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NotificationsTab;
